import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import colors from 'theme/colors';

const accent = colors.occasionalPurple;

// Horizontally scrollable row with prev/next arrows that disable at the ends.
export default function ScrollRow({ children, ariaLabel }) {
    const trackRef = useRef(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const update = useCallback(() => {
        const el = trackRef.current;
        if (!el) return;
        setCanLeft(el.scrollLeft > 1);
        setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    useEffect(() => {
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [update, children]);

    const scrollBy = (dir) => {
        const el = trackRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
    };

    return (
        <Wrapper>
            <Arrow type='button' aria-label='Anterior' disabled={!canLeft} onClick={() => scrollBy(-1)}>
                <ChevronLeft size={20} />
            </Arrow>
            <Track ref={trackRef} onScroll={update} role='group' aria-label={ariaLabel}>
                {children}
            </Track>
            <Arrow type='button' aria-label='Próximo' disabled={!canRight} onClick={() => scrollBy(1)}>
                <ChevronRight size={20} />
            </Arrow>
        </Wrapper>
    );
}

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const Track = styled.div`
    flex: 1;
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 2px;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
`;

const Arrow = styled.button`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 32px;
    padding: 0;
    border: none;
    background: none;
    color: ${accent};
    cursor: pointer;

    &:disabled { color: ${colors.nightLight}; opacity: 0.5; cursor: default; }
`;
